import { useState } from "react";
import hookIconUrl from "../../images/hook-icon.png";

function IconHook() {
  return (
    <span
      className="debug-bar__hook-icon"
      style={{
        WebkitMaskImage: `url(${hookIconUrl})`,
        maskImage: `url(${hookIconUrl})`,
      }}
      aria-hidden="true"
    />
  );
}

function IconRefresh() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M2.5 8a5.5 5.5 0 0 1 9.3-4.03M13.5 8A5.5 5.5 0 0 1 4.2 12.03"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M11.5 2.5V5h2.5M4.5 13.5V11H2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconCopy() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect
        x="5.5"
        y="5.5"
        width="8"
        height="8"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M10.5 5.5V4A1.5 1.5 0 0 0 9 2.5H4A1.5 1.5 0 0 0 2.5 4v5A1.5 1.5 0 0 0 4 10.5h1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3.5 8.5 6.5 11.5 12.5 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function shortenId(id) {
  if (!id) return "—";
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…`;
}

// The bar mirrors one access state at a time: a paid entitlement, a running
// trial, free minutes being spent, or those minutes exhausted.
function resolveAccessState({ isPro, trialMode, usageHit, usageProgress }) {
  if (isPro) return "upgraded";
  if (trialMode) return "trialing";
  if (usageHit || usageProgress >= 0.99) return "capped";
  return "using";
}

const ACCESS_STATES = [
  {
    key: "trialing",
    label: "Trialing",
    title: "Start a fresh trial",
    handler: "onStartTrialModeClick",
  },
  {
    key: "using",
    label: "Using",
    title: "End any trial and reset the free minutes",
    handler: "onUsingUsageClick",
  },
  {
    key: "capped",
    label: "Capped",
    title: "End any trial and exhaust the free minutes",
    handler: "onForceMaxUsageClick",
  },
  {
    // Pro comes from a real Stripe entitlement, so this segment cannot be
    // faked locally — clicking it opens the same checkout the popup does.
    key: "upgraded",
    label: "Upgraded",
    title: "Open checkout to buy a real entitlement",
    handler: "onUpgradeClick",
  },
];

export default function DebugBar({
  testMode,
  paymentHookId,
  hookBusy,
  hookError,
  isPro,
  trialMode,
  usageHit,
  usageProgress,
  onTestModeChange,
  onRefreshPaymentHookClick,
  onStartTrialModeClick,
  onUsingUsageClick,
  onForceMaxUsageClick,
  onUpgradeClick,
}) {
  const [copied, setCopied] = useState(false);
  const developmentControlsEnabled =
    process.env.NODE_ENV === "development";
  const accessState = resolveAccessState({
    isPro,
    trialMode,
    usageHit,
    usageProgress,
  });
  const accessHandlers = {
    onStartTrialModeClick,
    onUsingUsageClick,
    onForceMaxUsageClick,
    onUpgradeClick,
  };

  function onCopy() {
    if (!paymentHookId) return;
    navigator.clipboard.writeText(paymentHookId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="debug-bar">
      {developmentControlsEnabled && (
        <div className="debug-bar__group">
          <div
            className={`debug-bar__segmented${hookBusy ? " debug-bar__segmented--busy" : ""}`}
            role="group"
            aria-label="Payment mode"
          >
            <button
              type="button"
              className={`debug-bar__segment${!testMode ? " debug-bar__segment--active" : ""}`}
              disabled={hookBusy}
              aria-pressed={!testMode}
              onClick={() => !testMode || onTestModeChange(false)}
            >
              Live
            </button>
            <button
              type="button"
              className={`debug-bar__segment${testMode ? " debug-bar__segment--active" : ""}`}
              disabled={hookBusy}
              aria-pressed={testMode}
              onClick={() => testMode || onTestModeChange(true)}
            >
              Test
            </button>
          </div>

          <span className="debug-bar__sep" aria-hidden="true" />

          <span className="debug-bar__label">State:</span>
          <div
            className={`debug-bar__segmented${hookBusy ? " debug-bar__segmented--busy" : ""}`}
            role="group"
            aria-label="Access state"
          >
            {ACCESS_STATES.map(({ key, label, title, handler }) => (
              <button
                key={key}
                type="button"
                className={`debug-bar__segment${accessState === key ? " debug-bar__segment--active" : ""}`}
                disabled={hookBusy}
                aria-pressed={accessState === key}
                title={title}
                onClick={() => accessHandlers[handler]?.()}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="debug-bar__group debug-bar__group--grow">
        {hookError ? (
          <span className="debug-bar__error" title={hookError} role="alert">
            {hookError}
          </span>
        ) : (
          <div
            className={`debug-bar__chip${hookBusy ? " debug-bar__chip--busy" : ""}`}
            aria-busy={hookBusy || undefined}
          >
            <IconHook />
            <span className="debug-bar__chip-value" title={paymentHookId || undefined}>
              {shortenId(paymentHookId)}
            </span>
            <div className="debug-bar__actions" role="group" aria-label="Payment hook">
              <button
                type="button"
                className="debug-bar__action"
                title="Create new payment hook"
                aria-label="Create new payment hook"
                disabled={hookBusy}
                onClick={onRefreshPaymentHookClick}
              >
                <IconRefresh />
              </button>
              <button
                type="button"
                className={`debug-bar__action${copied ? " debug-bar__action--done" : ""}`}
                title={copied ? "Copied" : "Copy payment hook id"}
                aria-label={copied ? "Copied" : "Copy payment hook id"}
                disabled={!paymentHookId || hookBusy}
                onClick={onCopy}
              >
                {copied ? <IconCheck /> : <IconCopy />}
              </button>
            </div>
          </div>
        )}
      </div>

      {developmentControlsEnabled && (
        <span className="debug-bar__hash" title={process.env.COMMIT_HASH}>
          {process.env.COMMIT_HASH}
        </span>
      )}
    </div>
  );
}
